import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useT } from "@/i18n";
import { Activity, Check, Microscope, ShieldCheck } from "lucide-react";

const focusIcons = [Microscope, Activity, ShieldCheck];

const Conditions = () => {
  const t = useT(
    {
      eyebrow: "Schwerpunkte",
      heading: "Meine Schwerpunkte",
      intro:
        "Wenn Beschwerden komplexer sind, lohnt sich ein genauerer Blick. Chronische, wiederkehrende oder schwer einzuordnende Beschwerden entstehen selten völlig losgelöst voneinander. Darm, Schleimhäute, Immunsystem, Stoffwechsel, Hormone und mikrobielle Prozesse stehen in ständigem Austausch. In meiner Arbeit geht es darum, mögliche Zusammenhänge sichtbar zu machen und individuelle Ansatzpunkte zu finden.",
      topicsLabel: "Typische Themen & Beschwerdebilder",
      focuses: [
        {
          number: "01",
          title: "Mikrobiom & Schleimhautsystem",
          description:
            "Der Darm und unsere Schleimhäute sind kein isoliertes System. Sie stehen in stetem Austausch mit Immunsystem, Stoffwechsel, Hormonhaushalt und Nervensystem. Veränderungen im Mikrobiom oder an der Schleimhautbarriere können sich deshalb auf ganz unterschiedliche Beschwerdebilder auswirken – oft weit über den Verdauungstrakt hinaus.",
          topics: [
            "Darmbeschwerden und Verdauungsprobleme",
            "Reizdarm",
            "SIBO / IMO",
            "Dysbiosen",
            "Chronisch-entzündliche Darmerkrankungen wie Morbus Crohn und Colitis ulcerosa",
            "Histamin- und Nahrungsmittelunverträglichkeiten",
            "Divertikulitis",
            "Darm-Hirn-Achse",
            "Vaginal- und Urogenitalmikrobiom",
            "Schleimhautgesundheit",
          ],
        },
        {
          number: "02",
          title: "Chronische Beschwerden & Regulationsstörungen",
          description:
            "Chronische Beschwerden entstehen selten aus einer einzigen Ursache. Oft überlagern sich Entzündungen, Stoffwechselprozesse, hormonelle Dysbalancen, Immunantworten und mikrobielle Faktoren. Statt einzelne Symptome isoliert zu betrachten, geht es mir darum, Muster zu erkennen und mögliche Einflussfaktoren im Zusammenspiel zu verstehen.",
          topics: [
            "Chronische Entzündungen",
            "Autoimmunerkrankungen und Immunsystemfehlregulationen",
            "Allergien und Immunschwäche",
            "Hauterkrankungen wie Neurodermitis, Psoriasis, Urtikaria oder Akne",
            "Hormonelle Dysbalancen und Endometriose",
            "Schilddrüsenbeschwerden",
            "Chronische Schmerzen und unklare Schmerzzustände",
            "Fibromyalgie",
            "Rheumatologische Erkrankungen und reaktive Arthritis",
            "Stoffwechsel und Mitochondrienfunktion",
          ],
        },
        {
          number: "03",
          title: "Chronische & wiederkehrende Infektionen",
          description:
            "Wiederkehrende oder lange andauernde Infektionen haben oft mehr als eine Ursache. Biofilme, Dysbiosen, Resistenzen, das eigene Mikrobiom und individuelle Risikofaktoren können eine Rolle spielen. Besonders bei wiederholten Beschwerden lohnt sich ein differenzierter Blick – auch auf innovative mikrobiologische Ansätze.",
          topics: [
            "Wiederkehrende Harnwegsinfekte",
            "Chronische Blasenbeschwerden",
            "Persistierende und wiederkehrende Infektionen",
            "Reizblase",
            "Interstitielle Zystitis",
            "Chronische Prostatitis / CPPS",
            "Biofilme und pathogene Mikroorganismen",
            "Mikrobiologische Diagnostik",
            "Antibiotikaresistenzen und multiresistente Erreger",
          ],
        },
      ],
    },
    {
      eyebrow: "Focus areas",
      heading: "My focus areas",
      intro:
        "When complaints become more complex, a closer look is worthwhile. Chronic, recurring or difficult-to-classify complaints rarely arise completely independently of one another. Gut, mucous membranes, immune system, metabolism, hormones and microbial processes are in constant exchange. My work is about making possible connections visible and finding individual starting points.",
      topicsLabel: "Typical topics & health concerns",
      focuses: [
        {
          number: "01",
          title: "Microbiome & mucosal systems",
          description:
            "The gut and our mucous membranes are not an isolated system. They are in constant exchange with the immune system, metabolism, hormonal balance and nervous system. Changes in the microbiome or mucosal barrier can therefore affect very different health concerns – often far beyond the digestive tract.",
          topics: [
            "Bowel complaints and digestive problems",
            "Irritable bowel syndrome",
            "SIBO / IMO",
            "Dysbiosis",
            "Inflammatory bowel diseases such as Crohn’s disease and ulcerative colitis",
            "Histamine and food intolerances",
            "Diverticulitis",
            "Gut–brain axis",
            "Vaginal and urogenital microbiome",
            "Mucosal health",
          ],
        },
        {
          number: "02",
          title: "Chronic complaints & regulatory disorders",
          description:
            "Chronic complaints rarely arise from a single cause. Inflammation, metabolic processes, hormonal imbalances, immune responses and microbial factors often overlap. Rather than viewing individual symptoms in isolation, I focus on recognising patterns and understanding possible influencing factors in their interplay.",
          topics: [
            "Chronic inflammation",
            "Autoimmune conditions and immune dysregulation",
            "Allergies and immune deficiency",
            "Skin conditions such as eczema, psoriasis, urticaria or acne",
            "Hormonal imbalances and endometriosis",
            "Thyroid complaints",
            "Chronic pain and unexplained pain",
            "Fibromyalgia",
            "Rheumatological conditions and reactive arthritis",
            "Metabolism and mitochondrial function",
          ],
        },
        {
          number: "03",
          title: "Chronic & recurring infections",
          description:
            "Recurring or long-lasting infections often have more than one cause. Biofilms, dysbiosis, resistance, the individual microbiome and personal risk factors can all play a role. Especially with repeated complaints, a differentiated view is worthwhile – including innovative microbiological approaches.",
          topics: [
            "Recurring urinary tract infections",
            "Chronic bladder complaints",
            "Persistent and recurring infections",
            "Overactive bladder",
            "Interstitial cystitis",
            "Chronic prostatitis / CPPS",
            "Biofilms and pathogenic microorganisms",
            "Microbiological diagnostics",
            "Antibiotic resistance and multidrug-resistant pathogens",
          ],
        },
      ],
    },
  );

  return (
    <section id="beschwerden" className="bg-secondary/30 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <span className="text-sm font-medium uppercase tracking-wider text-primary">
            {t.eyebrow}
          </span>
          <h2 className="mt-2 mb-4 text-3xl font-bold text-foreground md:text-4xl">
            {t.heading}
          </h2>
          <p className="leading-relaxed text-muted-foreground">{t.intro}</p>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mx-auto grid max-w-5xl gap-5"
        >
          {t.focuses.map((focus, index) => {
            const Icon = focusIcons[index];

            return (
              <AccordionItem
                key={focus.number}
                value={`focus-${focus.number}`}
                className="overflow-hidden rounded-lg border border-border/70 bg-card px-5 shadow-sm transition-colors data-[state=open]:border-primary/30 md:px-7"
              >
                <div className="flex items-start gap-4 pt-5 md:gap-5 md:pt-7">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <span className="block text-xs font-semibold tracking-widest text-primary/70">
                      {focus.number}
                    </span>
                    <h3 className="text-xl font-semibold leading-snug text-foreground md:text-2xl">
                      {focus.title}
                    </h3>
                  </div>
                </div>

                <p className="mt-4 max-w-4xl leading-relaxed text-muted-foreground">
                  {focus.description}
                </p>

                <AccordionTrigger className="mt-2 min-h-11 py-4 text-left text-sm font-semibold text-primary hover:no-underline">
                  {t.topicsLabel}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pt-1 md:pb-7">
                  <ul className="grid gap-x-8 gap-y-3 border-t border-border/60 pt-5 sm:grid-cols-2">
                    {focus.topics.map((topic) => (
                      <li key={topic} className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
};

export default Conditions;